import { isDevPreview } from '../boot/devPreview';
import { sb } from './supabase';
import { FIXTURE_DRAWINGS, FIXTURE_MEMBERS, FIXTURE_PROJECTS } from './fixtures';
import { STANDARD_LAYERS } from '../cad/model/document';
import type {
  AppUserRow,
  Drawing,
  DrawingRow,
  DrawingVersion,
  MemberRole,
  Project,
  ProjectMember,
  ProjectStatus,
  ProjectSummary,
  ProjectType,
  ReviewQueueItem,
  VersionStatus,
} from './types';

/**
 * 저장소 계층.
 *
 * 화면은 Supabase 를 직접 호출하지 않고 전부 여기를 거친다.
 *   · 개발 미리보기(devshell) 분기가 한 곳에만 존재한다.
 *   · 쿼리 형태가 바뀌어도 화면을 건드리지 않는다.
 *   · 감사 로그 기록을 빠뜨리기 어렵다.
 */

function fail(where: string, error: { message: string } | null): void {
  if (error) throw new Error(`${where}: ${error.message}`);
}

/** 뷰가 돌려주는 평탄한 행을 화면용 중첩 구조로 바꾼다. */
interface DrawingRowRaw extends Drawing {
  is_favorite: boolean;
  latest_version_id: string | null;
  latest_version_number: number | null;
  latest_version_status: VersionStatus | null;
  latest_entity_count: number | null;
  latest_version_at: string | null;
  latest_version_by: string | null;
}

function toDrawingRow(r: DrawingRowRaw): DrawingRow {
  const latest: DrawingVersion | null = r.latest_version_id
    ? {
        id: r.latest_version_id,
        drawing_id: r.id,
        branch_id: r.current_branch_id ?? '',
        version_number: r.latest_version_number ?? 0,
        name: `v${r.latest_version_number ?? 0}`,
        change_summary: null,
        status: r.latest_version_status ?? 'draft',
        entity_count: r.latest_entity_count ?? 0,
        created_by: r.latest_version_by ?? '',
        created_at: r.latest_version_at ?? r.updated_at,
      }
    : null;

  return {
    id: r.id,
    project_id: r.project_id,
    name: r.name,
    drawing_type: r.drawing_type,
    floor_name: r.floor_name,
    zone_tags: r.zone_tags ?? [],
    unit: r.unit,
    current_branch_id: r.current_branch_id,
    published_version_id: r.published_version_id,
    thumbnail_path: r.thumbnail_path,
    created_by: r.created_by,
    created_at: r.created_at,
    updated_at: r.updated_at,
    is_favorite: r.is_favorite,
    latest_version: latest,
  };
}

/* ========================================================================== *
 * 프로젝트
 * ========================================================================== */

export async function listProjects(includeArchived = false): Promise<ProjectSummary[]> {
  if (isDevPreview()) {
    return FIXTURE_PROJECTS.filter((p) => includeArchived || p.status !== 'archived');
  }

  let q = sb()
    .from('cad_project_summary')
    .select('*')
    .order('updated_at', { ascending: false });

  if (!includeArchived) q = q.neq('status', 'archived');

  const { data, error } = await q;
  fail('프로젝트 목록 조회', error);
  return (data ?? []) as ProjectSummary[];
}

export async function getProject(id: string): Promise<ProjectSummary | null> {
  if (isDevPreview()) return FIXTURE_PROJECTS.find((p) => p.id === id) ?? null;

  const { data, error } = await sb()
    .from('cad_project_summary')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  fail('프로젝트 조회', error);
  return (data as ProjectSummary | null) ?? null;
}

export interface NewProject {
  name: string;
  project_type: ProjectType;
  store_code?: string | null;
  store_name?: string | null;
  description?: string | null;
}

export async function createProject(input: NewProject): Promise<Project> {
  if (isDevPreview()) {
    throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다. 실제 로그인 후 이용하세요.');
  }

  const { data, error } = await sb()
    .from('cad_project')
    .insert({
      name: input.name.trim(),
      project_type: input.project_type,
      store_code: input.store_code || null,
      store_name: input.store_name || null,
      description: input.description || null,
    })
    .select('*')
    .single();
  fail('프로젝트 생성', error);

  const project = data as Project;
  await logActivity('project.create', { project: project.id, payload: { name: project.name } });
  return project;
}

export async function updateProject(
  id: string,
  patch: Partial<Pick<Project, 'name' | 'project_type' | 'status' | 'store_code' | 'store_name' | 'description'>>
): Promise<void> {
  if (isDevPreview()) {
    throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다.');
  }
  const { error } = await sb().from('cad_project').update(patch).eq('id', id);
  fail('프로젝트 수정', error);
  await logActivity('project.update', { project: id, payload: patch });
}

export async function archiveProject(id: string): Promise<void> {
  await updateProject(id, { status: 'archived' as ProjectStatus });
}

/**
 * 프로젝트 삭제 — 안의 도면·버전·원본까지 전부 사라진다.
 *
 * 누가 지울 수 있는지는 cad_delete_project() 함수(0013)가 정한다: 시스템 관리자,
 * 또는 만든 사람이면서 안에 남의 도면도 배포본도 없을 때. 화면에서 막는 것과
 * 별개로 DB 에서도 막는다.
 *
 * ★ RPC 를 거치는 이유 — 단순 RLS 삭제로는 안 된다. cad_operation(연산 로그)
 *   이 version_id not null + cascade 라서, 프로젝트 삭제가 그 안의 도면 →
 *   버전 → 연산 로그까지 cascade 로 지우려 드는데 append-only 트리거가 이걸
 *   막는다. cad_delete_project() 안에서만 그 cascade 를 잠깐 허용한다(0013).
 *
 * ★ 되돌릴 수 없다. 대부분의 경우 보관(archive)이 맞다 — 호출자가 그 선택지를
 *   먼저 보여줘야 한다.
 */
export async function deleteProject(id: string, name: string): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 삭제할 수 없습니다.');

  // 지운 뒤에는 project_id 가 남지 않으므로(감사 로그 외래키가 set null) 미리 남긴다
  await logActivity('project.delete', { project: id, payload: { name } });

  const { error } = await sb().rpc('cad_delete_project', { p_project_id: id });
  fail('프로젝트 삭제', error);
}

/**
 * 도면 삭제 — 버전·원본·댓글까지 전부 사라진다.
 *
 * 권한은 cad_delete_drawing() 함수(0013)가 정한다: 프로젝트 관리자, 또는
 * 만든 사람이면서 현장 배포본이 아닐 때. RPC 를 거치는 이유는 deleteProject
 * 와 같다 — cad_operation cascade 를 그 함수 안에서만 허용한다.
 */
export async function deleteDrawing(id: string, name: string): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 삭제할 수 없습니다.');

  await logActivity('drawing.delete', { drawing: id, payload: { name } });

  const { error } = await sb().rpc('cad_delete_drawing', { p_drawing_id: id });
  fail('도면 삭제', error);
}

/* ========================================================================== *
 * 프로젝트 멤버
 * ========================================================================== */

export async function listMembers(projectId: string): Promise<ProjectMember[]> {
  if (isDevPreview()) return FIXTURE_MEMBERS.filter((m) => m.project_id === projectId);

  const { data, error } = await sb()
    .from('cad_project_member')
    .select('*')
    .eq('project_id', projectId)
    .order('role');
  fail('멤버 조회', error);
  return (data ?? []) as ProjectMember[];
}

export async function upsertMember(
  projectId: string,
  email: string,
  role: MemberRole
): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다.');

  const { error } = await sb()
    .from('cad_project_member')
    .upsert({ project_id: projectId, email: email.trim().toLowerCase(), role });
  fail('멤버 저장', error);
  await logActivity('member.upsert', { project: projectId, payload: { email, role } });
}

export async function removeMember(projectId: string, email: string): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다.');

  const { error } = await sb()
    .from('cad_project_member')
    .delete()
    .eq('project_id', projectId)
    .eq('email', email);
  fail('멤버 삭제', error);
  await logActivity('member.remove', { project: projectId, payload: { email } });
}

/* ========================================================================== *
 * 도면
 * ========================================================================== */

export async function listDrawings(projectId: string): Promise<DrawingRow[]> {
  if (isDevPreview()) return FIXTURE_DRAWINGS.filter((d) => d.project_id === projectId);

  const { data, error } = await sb()
    .from('cad_drawing_row')
    .select('*')
    .eq('project_id', projectId)
    .order('updated_at', { ascending: false });
  fail('도면 목록 조회', error);
  return ((data ?? []) as DrawingRowRaw[]).map(toDrawingRow);
}

/** 대시보드 — 최근 작업한 도면 */
export async function listRecentDrawings(limit = 8): Promise<DrawingRow[]> {
  if (isDevPreview()) return FIXTURE_DRAWINGS.slice(0, limit);

  const { data, error } = await sb()
    .from('cad_drawing_row')
    .select('*')
    .order('updated_at', { ascending: false })
    .limit(limit);
  fail('최근 도면 조회', error);
  return ((data ?? []) as DrawingRowRaw[]).map(toDrawingRow);
}

/** 대시보드 — 즐겨찾기 */
export async function listFavoriteDrawings(): Promise<DrawingRow[]> {
  if (isDevPreview()) return FIXTURE_DRAWINGS.filter((d) => d.is_favorite);

  const { data, error } = await sb()
    .from('cad_drawing_row')
    .select('*')
    .eq('is_favorite', true)
    .order('updated_at', { ascending: false });
  fail('즐겨찾기 조회', error);
  return ((data ?? []) as DrawingRowRaw[]).map(toDrawingRow);
}

export async function setFavorite(drawingId: string, on: boolean): Promise<void> {
  if (isDevPreview()) return; // 미리보기에서는 조용히 무시

  const client = sb();
  const email = (await client.auth.getSession()).data.session?.user.email?.toLowerCase();
  if (!email) throw new Error('세션이 없습니다.');

  if (on) {
    const { error } = await client
      .from('cad_drawing_favorite')
      .upsert({ drawing_id: drawingId, email });
    fail('즐겨찾기 추가', error);
  } else {
    const { error } = await client
      .from('cad_drawing_favorite')
      .delete()
      .eq('drawing_id', drawingId)
      .eq('email', email);
    fail('즐겨찾기 해제', error);
  }
}

export interface NewDrawing {
  project_id: string;
  name: string;
  floor_name?: string | null;
  drawing_type?: string;
  zone_tags?: string[];
  /**
   * 초기 본문. 기본 도면(cad_base_drawing)에서 복사해 시작할 때 쓴다.
   * 없으면 빈 도면으로 시작한다.
   *
   * ★ 여기서 **복사**하는 것이지 참조하는 게 아니다. 원본 기본 도면을 나중에
   *   고쳐도 이미 만들어진 도면은 영향을 받지 않아야 한다.
   */
  document?: unknown;
  /**
   * 기본 도면에서 복사해 시작할 때, 그 기본 도면의 id.
   *
   * ★ 본문(document)을 브라우저로 내려받아 다시 올리지 않는다. id 만 넘기면
   *   서버가 DB 안에서 복사한다 — 수 MB 짜리 매장 도면이 왕복하면서 8초 제한에
   *   걸려 '초기 버전 생성: canceling statement due to statement timeout' 으로
   *   끊기던 문제를 없앤 방식이다(0016번).
   */
  baseDrawingId?: string;
  /** 출처 기록용 — 어느 기본 도면에서 만들었는지 활동 로그에 남긴다 */
  baseDrawingName?: string;
}

/**
 * 도면 생성.
 * main 브랜치와 초기 버전(v1)을 함께 만들어 편집기가 곧바로 열리게 한다.
 *
 * ★ 네 번 나눠 부르지 않고 서버 함수 하나로 만든다(0016_cad_create_drawing.sql).
 *   예전에는 도면·브랜치·버전·현재브랜치를 각각 따로 넣었는데, 각 요청이 따로
 *   커밋되는 탓에 중간에서 실패하면 **버전 없는 껍데기 도면**이 남았다.
 *   (기본 도면 복사 중 시간 초과가 나서 실제로 두 건이 남는 일이 있었다.)
 *   한 트랜잭션으로 묶으면 실패해도 아무것도 남지 않는다.
 */
export async function createDrawing(input: NewDrawing): Promise<Drawing> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다.');

  const client = sb();

  const { data, error } = await client.rpc('cad_create_drawing', {
    p_project: input.project_id,
    p_name: input.name.trim(),
    p_floor: input.floor_name ?? null,
    p_type: input.drawing_type ?? 'floor_plan',
    p_zone_tags: input.zone_tags ?? [],
    p_base: input.baseDrawingId ?? null,
    // 기본 도면에서 만들 때는 본문을 넘기지 않는다 — 서버가 DB 안에서 복사한다.
    p_document: input.baseDrawingId ? null : ((input.document as object | undefined) ?? null),
  });
  if (error) {
    // 0016번 SQL 을 아직 안 돌렸으면 무슨 일인지 알 수 없는 메시지가 나온다 — 짚어 준다.
    if (/cad_create_drawing/.test(error.message) &&
        /not find|does not exist|schema cache/i.test(error.message)) {
      throw new Error(
        '도면 생성: 서버 함수가 없습니다. supabase/migrations/0016_cad_create_drawing.sql 을 먼저 실행하세요.'
      );
    }
    fail('도면 생성', error);
  }

  const row = (Array.isArray(data) ? data[0] : data) as
    | { drawing_id: string; branch_id: string }
    | undefined;
  if (!row?.drawing_id) throw new Error('도면 생성: 서버가 새 도면을 돌려주지 않았습니다.');

  const { data: drawing, error: e2 } = await client
    .from('cad_drawing')
    .select('*')
    .eq('id', row.drawing_id)
    .single();
  fail('도면 조회', e2);

  await logActivity('drawing.create', {
    project: input.project_id,
    drawing: row.drawing_id,
    payload: input.baseDrawingName
      ? { name: input.name.trim(), from: input.baseDrawingName }
      : { name: input.name.trim() },
  });

  return drawing as Drawing;
}

/* ========================================================================== *
 * 검토 대기
 * ========================================================================== */

export async function listMyReviewQueue(): Promise<ReviewQueueItem[]> {
  if (isDevPreview()) {
    return FIXTURE_DRAWINGS.filter((d) => d.latest_version?.status === 'in_review').map((d) => ({
      version_id: d.latest_version!.id,
      version_number: d.latest_version!.version_number,
      status: 'in_review',
      created_at: d.latest_version!.created_at,
      created_by: d.latest_version!.created_by,
      drawing_id: d.id,
      drawing_name: d.name,
      project_id: d.project_id,
      project_name: FIXTURE_PROJECTS.find((p) => p.id === d.project_id)?.name ?? '',
    }));
  }

  const { data, error } = await sb()
    .from('cad_my_review_queue')
    .select('*')
    .order('created_at', { ascending: true });
  fail('검토 대기 조회', error);
  return (data ?? []) as ReviewQueueItem[];
}

/* ========================================================================== *
 * 라이브러리
 * ========================================================================== */

export interface LayerTemplateRow {
  code: string;
  name: string;
}

/**
 * 전사 표준 레이어를 DB(`cad_layer_template`)에서 그대로 읽는다.
 *
 * ★ `model/document.ts` 의 `STANDARD_LAYERS` 상수(편집기 부팅용)와 별개의
 *   소스다 — 그 상수 주석에도 적혀 있듯 DB 와 손으로 맞춰 둔 것이라 어긋날
 *   수 있다. `cad_block.default_layer` 처럼 실제로 이 테이블을 FK 로 참조하는
 *   값을 고를 때는 상수가 아니라 여기서 읽은 값을 써야 "존재하지 않는 레이어
 *   코드를 등록하려 해서 FK 위반"이 나지 않는다.
 */
export async function listLayerTemplates(): Promise<LayerTemplateRow[]> {
  if (isDevPreview()) {
    return STANDARD_LAYERS.map((l) => ({ code: l.code, name: l.name }));
  }

  const { data, error } = await sb()
    .from('cad_layer_template')
    .select('code, name')
    .order('sort_order', { ascending: true });
  fail('레이어 목록 조회', error);

  return (data ?? []) as LayerTemplateRow[];
}

/* ========================================================================== *
 * 사용자 관리 (/admin)
 * ========================================================================== */

export async function listAppUsers(): Promise<AppUserRow[]> {
  if (isDevPreview()) {
    return [
      {
        email: 'dev@localhost',
        name: '개발 미리보기',
        role: 'admin',
        dept: 'DX전략부',
        store_code: null,
        active: true,
        created_at: new Date().toISOString(),
      },
      {
        email: 'other@trial-net.co.kr',
        name: '설계 담당',
        role: 'designer',
        dept: '점포개발팀',
        store_code: null,
        active: true,
        created_at: new Date().toISOString(),
      },
      {
        email: 'review@trial-net.co.kr',
        name: '운영 검토',
        role: 'reviewer',
        dept: '영업운영팀',
        store_code: null,
        active: false,
        created_at: new Date().toISOString(),
      },
    ];
  }

  const { data, error } = await sb()
    .from('cad_app_user')
    .select('email, name, role, dept, store_code, active, created_at')
    .order('created_at', { ascending: false });
  fail('사용자 목록 조회', error);
  return (data ?? []) as AppUserRow[];
}

export async function updateAppUser(
  email: string,
  patch: Partial<Pick<AppUserRow, 'role' | 'active' | 'dept' | 'store_code' | 'name'>>
): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 저장할 수 없습니다.');

  const { error } = await sb().from('cad_app_user').update(patch).eq('email', email);
  fail('사용자 수정', error);
  await logActivity('user.update', { payload: { email, ...patch } });
}

/* ========================================================================== *
 * 감사 로그
 * ========================================================================== */

export async function logActivity(
  action: string,
  opts: {
    project?: string;
    drawing?: string;
    version?: string;
    payload?: Record<string, unknown>;
  } = {}
): Promise<void> {
  if (isDevPreview()) return;

  // 감사 로그 실패가 본 작업을 되돌리게 해서는 안 된다. 조용히 삼키되 콘솔에는 남긴다.
  const { error } = await sb().rpc('cad_log', {
    p_action: action,
    p_project: opts.project ?? null,
    p_drawing: opts.drawing ?? null,
    p_version: opts.version ?? null,
    p_payload: opts.payload ?? {},
  });
  if (error) console.warn('[cad_log] 기록 실패:', action, error.message);
}
